import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { mapValues } from "lodash";
import { useCallback } from "react";

import parseStyle from "./parseStyle";
import useThemeProps from "./useThemeProps.jsx";

const TypographyBlockRoot = styled("div")`
  ${(props) => parseStyle(props.style, props.theme)}
`;

export default function TypographyBlock(props) {
  let defaultElementMapping = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    p: "body",
  };
  const theme = useTheme();
  let {
    children,
    as = "div",
    elementMapping = {},
    marginBefore = false,
    marginAfter = false,
    maxBlockMargin = null,
    maxLineLength = "80ch",
    align,
    ...restProps
  } = useThemeProps({
    props,
    name: "TypographyBlock",
  });
  elementMapping = { ...defaultElementMapping, ...elementMapping };
  Object.keys(theme.typography?.variants || {})?.forEach((variant) => {
    elementMapping[`.wsui-${variant}`] = variant;
  });

  let rootStyles = {
    ...theme.typography?.variants[elementMapping["p"]],
    marginBlock: 0,
  };
  if (!marginBefore) {
    rootStyles = {
      ...rootStyles,
      "& > :first-child:first-child": {
        marginBlockStart: 0,
      },
    };
  }
  if (!marginAfter) {
    rootStyles = {
      ...rootStyles,
      "& > :last-child:last-child": {
        marginBlockEnd: 0,
      },
    };
  }

  const applyMaxBlockMargin = useCallback(
    (style) => {
      if (maxBlockMargin == null || !style) {
        return style;
      }
      return mapValues(style, (value, key) =>
        [
          "marginBlock",
          "marginBlockStart",
          "marginBlockEnd",
          "marginTop",
          "marginBottom",
        ].includes(key)
          ? `min(${theme.getLength(value)}, ${theme.getLength(maxBlockMargin)})`
          : value,
      );
    },
    [maxBlockMargin, theme],
  );

  return (
    <TypographyBlockRoot
      as={as}
      css={css([
        {
          textAlign: align,
        },
        parseStyle(rootStyles, theme),
        ...Object.entries(elementMapping).map(([selector, variant]) =>
          parseStyle(
            {
              [`& > ${selector}`]: {
                maxWidth: theme.typography?.maxLineLength || maxLineLength,
                marginInlineStart: ["right", "end", "center"].includes(align)
                  ? "auto"
                  : null,
                marginInlineEnd: ["left", "start", "center"].includes(align)
                  ? "auto"
                  : null,
                ...applyMaxBlockMargin(theme.typography?.variants[variant]),
              },
            },
            theme,
          ),
        ),
      ])}
      {...restProps}
    >
      {children}
    </TypographyBlockRoot>
  );
}
